import { Edit } from "iconsax-react";
import React, { useState } from "react";
import { MEDICAL_ICONS } from "./medical_icons";
import { TreatmentEditor } from "./TreatmentEditor";
import { Gift } from "lucide-react";

export const TreatmentItem = ({ treatment, editing, isAdmin }) => {
  const [editTreatment, setEditTreatment] = useState(null);
  return (
    <>
      <div className="my-4 py-20 px-10 rounded-lg bg-[#f9f9f9] -mx-7 relative">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-28 items-stretch max-w-[1240px] mx-auto">
          <div>
            <div className="relative">
              <h2 className="inline text-3xl text-[#072e49] font-medium mr-3">
                {treatment.title}
              </h2>
              <sup
                className={`py-1 px-3 rounded-md border text-xs inline-block mt-0.5 ${
                  treatment.instock === "true"
                    ? "border-primary text-primary"
                    : "border-[tomato] text-[tomato]"
                }`}
              >
                {treatment.instock === "true" ? "Instock" : "Out of stock"}
              </sup>
              {treatment.discount && (
                <div className="absolute -left-7 -top-7 bg-[tomato] rounded-md px-2 py-1 -rotate-[35deg] text-white">
                  {treatment.discount}% Off
                </div>
              )}
            </div>
            <ul className="my-10">
              {treatment.description_list.map(({ text, icon }, index) => {
                const Icon = MEDICAL_ICONS.find((i) => icon === i.name).icon;
                return (
                  <li
                    key={index}
                    className="grid grid-cols-[20px_auto] items-start gap-x-2 my-5"
                  >
                    <Icon size={20} />
                    <span className="">{text}</span>
                  </li>
                );
              })}
            </ul>
            <span className="font-medium leading-relaxed block my-10 text-lg">
              {treatment.description}
            </span>
            <div>
              {treatment.payment_type === "subscription" ? (
                <SubscriptionPlan
                  subscriptionPlans={treatment.subscription_plans}
                  discount={treatment.discount}
                />
              ) : (
                <div>
                  <PriceItem
                    price={treatment.price}
                    pref=" one-time payment"
                    deleted={treatment.discount}
                  />
                  {treatment.discount && (
                    <DiscountPriceItem
                      price={treatment.price}
                      discount={treatment.discount}
                      className={"mt-5"}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="my-6">
              <button className="bg-black h-[50px] flex items-center justify-center w-full px-3 py-2 rounded-full font-medium text-[14px] hover:opacity-80 text-white">
                Get Started
              </button>
              {treatment.coupon_code && (
                <div className="mt-5 flex items-center justify-center">
                  <Gift size={20} />
                  <span className="text-sm ml-2 mr-1">{treatment.coupon_code}</span>
                  <span className="text-sm font-medium">
                    ({treatment.coupon_price_percentage}% off)
                  </span>
                </div>
              )}
            </div>
          </div>
          <img
            src={treatment.image_url}
            width={500}
            height={500}
            className="rounded-lg shadow-sm w-full"
          />
        </div>
        {isAdmin && (
          <div
            className="absolute right-7 top-7 cursor-pointer hover:text-primary transition duration-300"
            onClick={() => setEditTreatment(treatment)}
          >
            <Edit size={34} />
          </div>
        )}
      </div>
      {editTreatment && (
        <>
          <TreatmentEditor
            treatment={treatment}
            onClose={() => setEditTreatment(null)}
            editing={editing}
          />
          <div
            className="fixed inset-0 bg-black bg-opacity-20 z-40"
            onClick={() => setEditTreatment(null)}
          />
        </>
      )}
    </>
  );
};

const SubscriptionPlan = ({ subscriptionPlans, discount }) => {
  const [subscriptionPlan, setSubscriptionPlan] = useState(
    subscriptionPlans.quarterly ? "quarterly" : "monthly"
  );
  return (
    <>
      {subscriptionPlans.is_dose_static === "true" ? (
        <div>
          <PriceItem
            price={subscriptionPlans?.monthly?.monthly}
            pref="/mo, no matter the dose"
            deleted={discount}
          />
          {discount && (
            <DiscountPriceItem
              price={subscriptionPlans?.monthly?.monthly}
              discount={discount}
              className={"mt-5"}
            />
          )}
        </div>
      ) : (
        <>
          <div
            className={`grid ${
              subscriptionPlans.quarterly ? "grid-cols-2" : "grid-cols-1"
            } gap-x-2`}
          >
            {subscriptionPlans.quarterly && (
              <div
                className={`border border-primary py-4 px-2 w-full rounded-lg flex items-center justify-center mb-5 ${
                  subscriptionPlan === "quarterly"
                    ? "bg-primary-light"
                    : "bg-transparent"
                } duration-300 transition cursor-pointer hover:bg-primary-light`}
                onClick={() => setSubscriptionPlan("quarterly")}
              >
                Quarterly
              </div>
            )}
            {subscriptionPlans.monthly && (
              <div
                className={`border border-primary py-4 px-2 w-full rounded-lg flex items-center justify-center mb-5 ${
                  subscriptionPlan === "monthly"
                    ? "bg-primary-light"
                    : "bg-transparent"
                } duration-300 transition cursor-pointer hover:bg-primary-light`}
                onClick={() => setSubscriptionPlan("monthly")}
              >
                Monthly
              </div>
            )}
          </div>
          {subscriptionPlan === "quarterly" ? (
            <div
              className={`grid ${
                subscriptionPlans.quarterly?.first_month_pay
                  ? "grid-cols-2"
                  : "grid-cols-1"
              } gap-x-2 text-center`}
            >
              {subscriptionPlans.quarterly?.first_month_pay && (
                <div>
                  <PriceItem
                    price={subscriptionPlans.quarterly?.first_month_pay}
                    pref=" first month"
                    deleted={discount}
                  />
                  {discount && (
                    <DiscountPriceItem
                      price={subscriptionPlans.quarterly?.first_month_pay}
                      discount={discount}
                      className={"mt-5"}
                    />
                  )}
                </div>
              )}
              {subscriptionPlans.quarterly?.monthly && (
                <div>
                  <PriceItem
                    pref={"/mo"}
                    price={subscriptionPlans.quarterly.monthly}
                    deleted={discount}
                  />
                  {discount && (
                    <DiscountPriceItem
                      price={subscriptionPlans.quarterly.monthly}
                      discount={discount}
                      className={"mt-5"}
                    />
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <PriceItem
                pref={"/mo"}
                price={subscriptionPlans?.monthly?.monthly}
                deleted={discount}
                isCenter={true}
              />
              {discount && (
                <DiscountPriceItem
                  price={subscriptionPlans?.monthly?.monthly}
                  discount={discount}
                  className={"mt-5 text-center"}
                />
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

const PriceItem = ({ price, pref, isCenter, deleted }) => {
  return (
    <div
      className={`text-lg ${isCenter ? "text-center" : ""} ${
        deleted ? "opacity-60" : ""
      }`}
    >
      <strong className={deleted ? "line-through" : ""}>
        ${price?.toLocaleString()}
      </strong>
      {pref}
    </div>
  );
};

const DiscountPriceItem = ({ price, discount, isCenter, className }) => {
  const discountedPrice = Math.floor((+price / 100) * +discount);
  const newPrice = +price - discountedPrice;
  return (
    <div className={`text-lg ${isCenter ? "text-center" : ""} ${className}`}>
      Discount price: <strong>${newPrice.toLocaleString()}</strong>
    </div>
  );
};
